/**
 * Writing figured bass under the music.
 *
 * A stack is typed, not picked from a list -- `6`, `6/4`, `#6/5` -- for the same reason a
 * chord symbol is: there are more stacks than any palette could hold, and a continuo player
 * types them faster than they could find them. The model reads what was typed and prints it
 * back, and this layer decides *where*: under the note the user is on, at the tick that note
 * begins, in that note's bar.
 *
 * A stack already standing on that tick is replaced rather than joined, because two stacks
 * on one beat would be drawn on top of each other.
 *
 * Nothing here transposes, and nor does anything else: a figure is an interval above the
 * bass, and the interval above the bass is the same in every key. That is the whole
 * difference between a figure and a chord symbol, and it is why transposing a part moves
 * its symbols and leaves its figures exactly as they were written.
 */

import type { Command } from "../commands/types";
import { parseFigures } from "../document/figures";
import type { FiguredBass } from "../document/figures";
import type { Score } from "../document/types";
import { barOf, coveredBars, covers, rewriteBar, tickInBar } from "./bar-marks";
import { focusLocation } from "./selection";
import type { Selection } from "./selection";

/**
 * Put a stack of figures under the note the selection is on.
 *
 * The text is read here rather than by the caller so that a host can hand over exactly what
 * was typed and show the refusal it gets back: `6/4` and `6\\4` fail in different ways and
 * whoever typed them is owed the difference.
 */
export function set(score: Score, selection: Selection, text: string): Command | string {
  const at = focusLocation(score, selection);
  if (at === null) return "select a bass note to write the figure under";
  const bar = barOf(score, at);
  if (bar === null) return "that part has no bar there";

  const read = parseFigures(text);
  if (typeof read === "string") return read;

  const onset = tickInBar(score, at);
  const stack: FiguredBass = { ...read, onset };
  const kept = bar.figures.filter((existing) => existing.onset !== onset);
  const figures = [...kept, stack].sort((left, right) => left.onset - right.onset);
  return rewriteBar(at, bar, { figures });
}

/** Take away the figures standing under the selected music. */
export function clear(score: Score, selection: Selection): Command | string {
  const bars = coveredBars(score, selection);
  if (bars.length === 0) return "no note is selected";

  const commands: Command[] = [];
  for (const { at, ranges } of bars) {
    const bar = barOf(score, at);
    if (bar === null) continue;
    const figures = bar.figures.filter((stack) => !covers(ranges, stack.onset));
    if (figures.length === bar.figures.length) continue;
    commands.push(rewriteBar(at, bar, { figures }));
  }

  if (commands.length === 0) return "there is no figure under the selected notes";
  return { kind: "batch", label: "remove the figured bass", commands };
}

/** The stack standing exactly where the selection is, which is what a field shows. */
export function at(score: Score, selection: Selection): FiguredBass | null {
  const focus = focusLocation(score, selection);
  if (focus === null) return null;
  const onset = tickInBar(score, focus);
  return barOf(score, focus)?.figures.find((stack) => stack.onset === onset) ?? null;
}
